import { createFileRoute } from '@tanstack/react-router'
import AuthButtons from '../components/AuthButtons'
import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import type { OAuthProvider } from '../services/auth'
import { motion } from 'framer-motion'

export const Route = createFileRoute('/signin')({
  component: SignIn,
})

function SignIn() {
  const [showSignup, setShowSignup] = useState(false)
  const { login } = useAuth()

  const handleSignup = (provider: OAuthProvider) => {
    login(provider, true)
  }

  useEffect(() => {
    // Check if we have a USER_NOT_FOUND error from a failed signin attempt
    const loginMessage = localStorage.getItem('login:message')
    if (loginMessage) {
      try {
        const message = JSON.parse(loginMessage)
        if (message.type === 'USER_NOT_FOUND') {
          setShowSignup(true)
          localStorage.removeItem('login:message')
        }
      } catch {
        // Ignore parse errors
      }
    }
  }, [])

  return (
    <div style={{ minHeight: '80vh', display: 'grid', placeItems: 'center', padding: 20 }}>
      <div style={{ textAlign: 'center' }}>
        <h1 style={{ marginBottom: 16 }}>{showSignup ? 'Create an Account' : 'Sign in'}</h1>
        {showSignup ? (
          <div>
            <p style={{ marginBottom: 20, color: '#666' }}>
              Account not found. Please sign up to continue.
            </p>
            <div style={{ display: 'grid', gap: '12px' }}>
              <OAuthButton
                provider="google"
                onClick={() => handleSignup('google')}
                color="#ea4335"
                text="Sign up with Google"
                variant="signup"
              />
              <OAuthButton
                provider="github"
                onClick={() => handleSignup('github')}
                color="#24292e"
                text="Sign up with GitHub"
                variant="signup"
              />
              <OAuthButton
                provider="linkedin"
                onClick={() => handleSignup('linkedin')}
                color="#0A66C2"
                text="Sign up with LinkedIn"
                variant="signup"
              />
            </div>
          </div>
        ) : (
          <AuthButtons />
        )}
      </div>
    </div>
  )
}

interface OAuthButtonProps {
  provider: OAuthProvider
  onClick: () => void
  color: string
  text: string
  variant?: 'login' | 'signup'
}

function OAuthButton({ provider, onClick, text }: OAuthButtonProps & { provider: OAuthProvider }) {
  return (
    <motion.button
      onClick={onClick}
      whileHover={{ scale: 1.02, y: -1 }}
      whileTap={{ scale: 0.98 }}
      className="oauth-btn"
    >
      <span className="oauth-btn__icon">
        {loginIcon(provider)}
      </span>
      <span className="oauth-btn__label">{text}</span>
    </motion.button>
  )
}

function loginIcon(p: OAuthProvider) {
  switch (p) {
    case 'google':
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="10" cy="10" r="10" fill="#4285F4"/>
          <text x="10" y="14" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">G</text>
        </svg>
      )
    case 'github':
      return (
        <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
          <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.2 1.87.86 2.33.65.07-.52.28-.86.51-1.06-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.01.08-2.1 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.09.16 1.9.08 2.1.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.19 0 .21.15.46.55.38C13.71 14.53 16 11.54 16 8c0-4.42-3.58-8-8-8z"/>
        </svg>
      )
    case 'linkedin':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="4" fill="#0A66C2" />
          <path d="M7.5 6.75a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm-1.25 4.25h2.5v7.5h-2.5v-7.5Zm4 0h2.4v1.03h.03c.33-.62 1.15-1.27 2.38-1.27 2.55 0 3.02 1.68 3.02 3.86v3.88h-2.5v-3.44c0-.82-.02-1.87-1.14-1.87-1.14 0-1.32.89-1.32 1.81v3.5h-2.5v-7.5Z" fill="#fff" />
        </svg>
      )
  }
}
